import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Check, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const CustomerConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get("token");
      console.log("🔍 Token from URL:", token);

      if (!token) {
        setStatus("error");
        setMessage("Ungültiger Bestätigungslink");
        return;
      }

      try {
        console.log("🔄 Calling verify_customer_consent with token:", token);
        // Use RPC function to verify (bypasses RLS)
        const { data, error } = await (supabase as any)
          .rpc("verify_customer_consent", { token_param: token }) as { 
            data: { success: boolean; message: string } | null; 
            error: any 
          };

        console.log("📊 RPC Response:", { data, error });

        if (error) {
          console.error("❌ Verification error:", error);
          console.error("Error details:", JSON.stringify(error, null, 2));
          setStatus("error");
          setMessage("Die Bestätigung konnte nicht verarbeitet werden.");
          return;
        }

        if (data?.success) {
          console.log("✅ Verification successful!");
          setStatus("success");
          setMessage("Vielen Dank für Ihre Bestätigung!");
          
          // Close window immediately
          window.close();
        } else {
          console.log("⚠️ Verification failed:", data?.message);
          setStatus("error");
          setMessage(data?.message || "Die Bestätigung konnte nicht verarbeitet werden. Möglicherweise wurde der Link bereits verwendet.");
        }
      } catch (error) {
        console.error("❌ Verification catch error:", error);
        setStatus("error");
        setMessage("Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.");
      }
    };

    verifyToken();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
            <h1 className="text-2xl font-bold mb-2">Wird verarbeitet...</h1>
            <p className="text-muted-foreground">
              Bitte warten Sie, während wir Ihre Bestätigung verarbeiten.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-green-600">
              Bestätigung erfolgreich!
            </h1>
            <p className="text-muted-foreground mb-4">
              {message}
            </p>
            <div className="bg-primary/5 rounded-lg p-4 mb-6">
              <p className="text-sm">
                Ihre Daten wurden erfolgreich in unserem System aufgenommen. 
                Sie werden nun professionell und effizient von unserem Team betreut.
              </p>
            </div>
            <div className="space-y-2 text-left text-sm text-muted-foreground">
              <p>✓ Ihre Daten sind sicher gespeichert</p>
              <p>✓ DSGVO-konforme Verarbeitung</p>
              <p>✓ Effiziente Betreuung durch unser Team</p>
            </div>
            <Button 
              onClick={() => window.close()} 
              className="mt-6 w-full"
            >
              Fenster schließen
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-red-600">
              Fehler bei der Bestätigung
            </h1>
            <p className="text-muted-foreground mb-6">
              {message}
            </p>
            <Button 
              onClick={() => window.close()} 
              variant="outline"
              className="w-full"
            >
              Fenster schließen
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerConfirmation;
